import { Errors } from '@/types';

export function hasPermissions(permissionsA: string[] | string, permissionsB: string[]): boolean | undefined {
  if (!permissionsA?.length || (!permissionsA?.length && !permissionsB?.length)) { return; }
  if (permissionsA?.length > 0 && !permissionsB?.length) { return false; }
  return Array.isArray(permissionsA)
    ? permissionsA.some((permission) => permissionsB.includes(permission))
    : permissionsB.includes(permissionsA);
}

export function hasRoutePermissions(permissionsA: string[] | string, permissionsB: string[]): boolean | undefined {
  if (!permissionsA?.length || (!permissionsA?.length && !permissionsB?.length)) { return; }
  return Array.isArray(permissionsA)
    ? permissionsA.some((permission) => permissionsB.includes(permission))
    : permissionsB.includes(permissionsA);
}

export interface Meta {
  permission?: string;
  [key: string]: any;
}

export interface Tree {
  children?: Tree[];
  name: string;
  meta: Meta;
  canSee?: boolean;
  parent?: Tree;
  canRemove?: boolean;
  [key: string]: any;
}

export function mergeDeepWithKey({oldNavigation, newNavigation, permissions}) {
  if (!newNavigation) { return oldNavigation; }

  const mergeLeftRoute = (route1, route2, children = []) => {
    const stubMeta = {
      link_name: '',
    };
    return {
      ...route1,
      path: route1.path,
      meta: {
        ...stubMeta,
        ...route1.meta,
        ...route2.meta,
        hasPermission: hasRoutePermissions(route2.meta?.permission, permissions),
      },
      children,
    };
  };

  const createMapRecursivity = (navigation, map = new Map()) => {
    navigation.forEach((route) => {
      if (!!route.name) {
        map.set(route.name, route);
        if (Array.isArray(route.children) && !!route.children.length) {
          return createMapRecursivity(route.children, map);
        }
      }
    });
    return map;
  };

  const mapedNavigation = createMapRecursivity(newNavigation);

  const merge = (old) => old.map((x) => {
    const route = mapedNavigation.get(x.name);
    if (!!route) {
      if (Array.isArray(x.children) && !!x.children.length) {
        const subRoute = merge(x.children);
        return mergeLeftRoute(x, route, subRoute);
      }
      return mergeLeftRoute(x, route);
    } else {
      if (!!x.name) {
        const message = `Missing config for page ${x.name}`;
        console.error(message);
      }
      return x;
    }
  });

  return merge(oldNavigation);
}

/**
 *
 * @param {number} value
 * @returns {string} custom currency format
 * @example
 *  // return 100 000 000 000.10
 *  currencyFormat(100000000000.1)
 *  // return 100 000
 *  currencyFormat(100000.00)
 */
export const currencyFormat = (value: number): string => {
  return (
    value
      .toFixed(2)
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ')
      .replace('.00', '')
  );
};

/**
 *
 * @param {obj} {title, description}
 * @description set metadata for SEO
 */
export const setMeta = ({title, description}) => {
  if (!!description) {
    const previous = document.head.querySelector('[name=description]');
    if (!!previous) { document.head.removeChild(previous); }
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = description;
    document.head.appendChild(metaDescription);
  }
  if (!!title) {document.title = title; }
};


/**
 *
 * @param {*} errors
 * @param {*} enums
 * @returns Errors[]
 */
export const mapErrors = (errors: Array<Pick<Errors, 'field' | 'messages'>>, enums: {}): Errors[] => {
  return  errors.map((error) => {
    return {
      ...error,
      fieldTranslation: enums[error.field],
    };
  });
};


/**
 * @param {string} str
 * @param {object} obj
 * @returns {string}
 * @description apply variables for string with template literals
 * @example
 * // return 11 less then 32
 * const str = '${value} less then ${max}';
 * const value = 11;
 * const max = 32;
 * applyInterpolation(str, {max, value});
 */
export const applyInterpolation = (str: string, obj: object): string => {
  return new Function(Object.keys(obj).toString(), `return \`${str}\`;`)
    .call(null, ...Object.values(obj));
};


/**
 * @param {string} str
 * @param {number} count
 * @returns {string}
 * @description
 * str pattern should be
 * "world | world | world | ${count} world"' =>
 *  for 0 count | for 1 count | for 2..9 count | for more then 10 count
 */
export const pluralize = (str: string, count: number): string => {
  if (typeof str !== 'string' || !str.length) { return str; }
  const re = /\s*(?:\||$)\s*/;
  const array = str.split(re);
  if (array.length < 4 || !array[3].includes('${count}')) {
    const UnexpectedPattern = function(this: any, massage: string) {
      this.message = massage;
      this.name = 'UnexpectedPattern';
      if (Error.captureStackTrace) {
        this.stack = Error.captureStackTrace(this);
      }
    };
    throw new UnexpectedPattern('The pattern must be "world | world | world | ${count} world"');
  }
  if (count === 0 || count === 1) {
    return array[count];
  } else if (count >= 2 && count < 5) {
    return array[2];
  } else if (count >= 5) {
    return applyInterpolation(array[3], {count});
  } else {
    return '';
  }
};
